import { CircularProgress } from "@mui/material";
import { CustomButton } from "../ui/CustomButton";
import { useEffect, useState } from "react";
import { campaignsApi, useUpdateExternalDataMutation } from "../../api";
import { useAppDispatch, useAppSelector } from "../../hooks/useReduxHooks";
import { AnalysisStatus, SSEEvents } from "../../types/companyTypes";
import { setIsNotificationsOpen, setStatus } from "../../features/companySlice";

type Props = {
  account_id: string;
  campaign_id: string;
  status: string;
  scrapingUpdateStatus: string;
  progress: number;
  isDisabled: boolean;
};

export const UpdateExternalDataBtn = ({
  account_id,
  campaign_id,
  status,
  scrapingUpdateStatus,
  progress,
  isDisabled,
}: Props) => {
  const dispatch = useAppDispatch();
  const { analysis_status, scraping_update_status } = useAppSelector(
    (state) => state.company.status,
  );

  const [currentStatus, setCurrentStatus] = useState<string>(status);
  const [currentScrapingUpdateStatus, setCurrentScrapingUpdateStatus] =
    useState<string>(scrapingUpdateStatus);
  const [currentProgress, setCurrentProgress] = useState<number>(progress);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAnalyses, setIsAnalyses] = useState<boolean>(false);

  const [updateExternalData, { isLoading: findExternalDataIsLoading }] =
    useUpdateExternalDataMutation();

  useEffect(() => {
    setCurrentStatus(status);
  }, [status]);

  useEffect(() => {
    setCurrentScrapingUpdateStatus(scrapingUpdateStatus);
  }, [scrapingUpdateStatus]);

  useEffect(() => {
    setCurrentProgress(progress);
  }, [progress]);

  useEffect(() => {
    if (scraping_update_status?.account_id === account_id) {
      setCurrentScrapingUpdateStatus(scraping_update_status.status);
    }
  }, [scraping_update_status]);

  useEffect(() => {
    if (analysis_status?.account_id === account_id) {
      setCurrentStatus(analysis_status.status);
      setCurrentProgress(analysis_status.progress);
    }
  }, [analysis_status]);

  useEffect(() => {
    findExternalDataIsLoading && setIsLoading(findExternalDataIsLoading);
  }, [findExternalDataIsLoading]);

  useEffect(() => {
    if (
      [AnalysisStatus.queued, AnalysisStatus.in_progress].some(
        (status) => status === currentScrapingUpdateStatus,
      )
    )
      setIsLoading(true);

    if (
      AnalysisStatus.completed === currentScrapingUpdateStatus &&
      AnalysisStatus.in_progress === currentStatus
    ) {
      setIsAnalyses(true);
    }

    if (AnalysisStatus.completed_no_data === currentScrapingUpdateStatus) {
      setIsLoading(false);
      setIsAnalyses(false);
      dispatch(setStatus({ [SSEEvents.scraping_update_status]: undefined }));
    }

    if (
      [AnalysisStatus.ready, AnalysisStatus.failed].some(
        (status) => status === currentScrapingUpdateStatus,
      ) ||
      (AnalysisStatus.completed === currentScrapingUpdateStatus &&
        AnalysisStatus.completed === currentStatus)
    ) {
      setIsLoading(false);
      setIsAnalyses(false);
      dispatch(
        setStatus({
          [SSEEvents.scraping_update_status]: undefined,
          [SSEEvents.analysis_status]: undefined,
        }),
      );
    }
  }, [currentScrapingUpdateStatus, currentStatus]);

  useEffect(() => {
    return () => {
      dispatch(
        setStatus({
          [SSEEvents.scraping_update_status]: undefined,
          [SSEEvents.analysis_status]: undefined,
        }),
      );
    };
  }, []);

  const startScrappingHandler = () => {
    if (isLoading) return;
    updateExternalData({ account_id, campaign_id })
      .unwrap()
      .then(() => {
        dispatch(campaignsApi.util.invalidateTags(["company", "members"]));
        dispatch(setIsNotificationsOpen(true));
      });
  };

  return (
    <CustomButton
      onClick={startScrappingHandler}
      customSize="fit"
      customColor="#F16300"
      customGradientColor="#FE8836"
      disabled={isLoading || isAnalyses || isDisabled}
    >
      {(isLoading || isAnalyses) && (
        <CircularProgress size={20} color="inherit" />
      )}
      {isAnalyses
        ? `Analyzing: ${currentProgress} %`
        : "Validate Contacts Data"}
    </CustomButton>
  );
};
